import { stampOf } from "@/lib/day";
import type { ReactNode } from "react";
import {
  AppWindow,
  CircleX,
  Clock,
  Download,
  FileText,
  TriangleAlert,
} from "lucide-react";
import type {
  GenerateRunResponse,
  RunTermLock,
  ServedStop,
  StageProgress,
} from "@patent-studio/api-types";
import { Button, buttonChrome } from "@/components/ui/button";
import { IconTile } from "@/components/ui/IconTile";
import { CAPTION_LINE } from "@/components/ui/marks";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Note } from "@/components/ui/Note";
import { Panel } from "@/components/ui/Panel";
import { ProgressBar } from "@/components/ui/ProgressBar";
import { Spinner } from "@/components/ui/Spinner";
import { MaybeLink } from "@/v2/layout/MaybeLink";
import { StagePage } from "@/v2/layout/StagePage";
import { findStage, stagePath } from "@/v2/stage-vocabulary";
import { DRAFTING, stageLabel } from "./run-copy";
import { SurveyCard } from "./SurveyCard";

/** Try again on a failed run: start it again, and what the server said if it refused. */
export interface RetryControl {
  onRetry: () => void;
  pending: boolean;
  /** The server's words for a refused start, or null. */
  refusal: string | null;
}

export interface DownloadControl {
  onDownload: () => void;
  pending: boolean;
  failed: boolean;
  /** The wait for the run's zip has run out with no zip. */
  overdue: boolean;
}

const EYEBROW = "Generate Patent Draft";

function Fact({
  icon,
  title,
  detail,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <Panel as="li" shape="card" pad="snug" className="flex items-start gap-3.5">
      <IconTile tone="brand">{icon}</IconTile>
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-[13px] text-muted-foreground">{detail}</p>
      </div>
    </Panel>
  );
}

/** The stage in flight, or the last one reached, and how many are done. */
function progressOf(progress: readonly StageProgress[]) {
  const current =
    progress.find((stage) => stage.state === "running") ??
    [...progress].reverse().find((stage) => stage.state === "done");
  return {
    label: current === undefined ? DRAFTING : stageLabel(current.stage_id),
    done: progress.filter((stage) => stage.state === "done").length,
  };
}

/**
 * When the start locked the terms, how many, and how many were edited from the
 * review, in v0.28's words under the processing message.
 */
function lockLine(lock: RunTermLock): string {
  const edits = lock.edited?.length ?? 0;
  const edited =
    edits === 0
      ? "no edits made from this step"
      : `${String(edits)} edited from this step`;
  return `Terminology locked ${stampOf(lock.locked_at)} · ${String(lock.term_count)} terms · ${edited}`;
}

function Processing({
  run,
  unreachable,
}: {
  run: GenerateRunResponse;
  unreachable: boolean;
}) {
  const progress = progressOf(run.progress);
  return (
    <StagePage
      align="center"
      eyebrow={EYEBROW}
      mark={
        <div className="flex flex-col items-center gap-5">
          <Spinner size="lg">
            <FileText />
          </Spinner>
          <span className="flex items-center gap-1.5 text-brand-mid">
            <MicroLabel className="font-mono text-brand-mid">
              Generating
            </MicroLabel>
            <Spinner size="dots" />
          </span>
        </div>
      }
      title="Your patent draft is being created"
      lede={
        run.state === "queued" ? "Waiting for a free drafting slot…" : undefined
      }
    >
      {run.term_lock !== null && (
        <p className="mt-1.5 text-center text-[12px] text-muted-foreground">
          {lockLine(run.term_lock)}
        </p>
      )}
      {unreachable && (
        <Note tone="warning" className="mx-auto mt-2 max-w-[520px]">
          We cannot reach the server right now. Your draft is still being
          generated.
        </Note>
      )}
      {run.state === "running" && (
        <ProgressBar
          className="mx-auto mt-4 max-w-[420px]"
          value={progress.done}
          max={run.progress.length}
          label={progress.label}
        />
      )}
      <ul className="mx-auto mt-6 flex max-w-[620px] flex-col gap-3 text-left">
        <Fact
          icon={<Clock />}
          title="This can take up to 20 minutes."
          detail="Longer disclosures with many figures sit at the upper end."
        />
        <Fact
          icon={<AppWindow />}
          title="It is safe to close this browser tab."
          detail="Generation continues on our servers whether or not you stay here."
        />
      </ul>
    </StagePage>
  );
}

/**
 * What the zip holds, each file named in bold, as v0.28 lists them. The
 * drawings are named only when the run made them: a matter with no placed
 * figure has none to file.
 */
function Includes({ drawings }: { drawings: boolean }) {
  return (
    <>
      <p className="mt-5 text-[15px] font-bold">Download file includes:</p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm marker:text-brand-mid">
        <li>
          <strong>The patent draft document</strong> (.docx)
        </li>
        <li>
          <strong>Decision record</strong> (.pdf): How the draft was produced:
          the decisions made and the sources behind them.
        </li>
        <li>
          <strong>Figures log</strong> (.pdf): Every figure in one document:
          components, descriptions and changes made.
        </li>
        {drawings && (
          <li>
            <strong>Drawings</strong> (.pdf): The drawing sheets to file with
            the application, one page per sheet.
          </li>
        )}
      </ul>
    </>
  );
}

/** The one download, large on the card's right, held while the zip is prepared. */
function DownloadAct({
  bundled,
  download,
}: {
  bundled: boolean;
  download: DownloadControl;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <Button
        variant="brand"
        size="hero"
        held={!bundled}
        onClick={download.onDownload}
        disabled={download.pending}
      >
        {bundled ? <Download /> : <Spinner size="sm" />}
        {!bundled
          ? "Preparing your download…"
          : download.pending
            ? "Preparing download…"
            : "Download Patent Draft (.zip)"}
      </Button>
      {bundled && download.failed && (
        <Note tone="destructive" role="alert">
          The download could not start. Try again.
        </Note>
      )}
      {!bundled && download.overdue && (
        <Note>
          The download is taking longer than usual. Reload this page in a few
          minutes.
        </Note>
      )}
    </div>
  );
}

function Ready({
  taskId,
  bundled,
  drawings,
  download,
}: {
  taskId: string;
  bundled: boolean;
  /** Whether the run made the drawings, which the zip then holds. */
  drawings: boolean;
  download: DownloadControl;
}) {
  return (
    <StagePage
      eyebrow={EYEBROW}
      title="Your patent draft is ready!"
      lede="Your patent draft is available to download below as a .docx file, so you can make any amendments you need. The Decision record and Figures documents are also available as a record of the process that went into generating the draft."
    >
      <Panel
        shape="card"
        pad="roomy"
        cap
        className="mt-6 grid items-center gap-8 md:grid-cols-[minmax(0,1fr)_auto]"
      >
        <div>
          <MicroLabel className="font-mono text-brand-mid">
            Your draft
          </MicroLabel>
          <div className="mt-3 flex items-start gap-3.5">
            <IconTile tone="brand" size="md">
              <FileText />
            </IconTile>
            <div>
              <h2 className="text-[22px] font-bold">Completed draft</h2>
              <p className="max-w-[460px] text-sm text-muted-foreground">
                The finished patent draft, assembled from the information you
                provided across every stage, ready for you to review.
              </p>
            </div>
          </div>
          <Includes drawings={drawings} />
        </div>
        <DownloadAct bundled={bundled} download={download} />
      </Panel>
      <SurveyCard taskId={taskId} />
    </StagePage>
  );
}

function Ended({
  icon,
  title,
  line,
  children,
}: {
  icon: ReactNode;
  title: string;
  line: string;
  children?: ReactNode;
}) {
  return (
    <StagePage
      align="center"
      eyebrow={EYEBROW}
      mark={icon}
      title={title}
      lede={line}
    >
      {children}
    </StagePage>
  );
}

/** What a start the server refused said, under the button that asked. */
function Refusal({ retry }: { retry: RetryControl }) {
  return retry.refusal === null ? null : (
    <Note tone="destructive" role="alert" className="mt-2">
      {retry.refusal}
    </Note>
  );
}

/**
 * A run a check stopped: the section by its own title where the framework
 * gives it one, what was found, the rule in the words it was written in, and
 * the stage that owns the fix. Generate again keeps the inputs and asks the
 * model afresh for the section that stopped, so a slip of the writer's is
 * often gone on the second run; the page says where to look if it is not.
 */
function Stopped({
  stop,
  taskId,
  retry,
}: {
  stop: ServedStop;
  taskId: string;
  retry: RetryControl;
}) {
  const stage = stop.fix_stage;
  const section = stop.section_title ?? stop.section_label;
  const stageName = findStage(stage)?.label ?? stage;
  return (
    <Ended
      title="The draft stopped before it finished"
      line={`Praxis could not complete "${section}": ${stop.detail}`}
      icon={
        <IconTile tone="warning" shape="round" size="md">
          <TriangleAlert />
        </IconTile>
      }
    >
      {stop.rule_text !== null && (
        <p className={`mx-auto mt-1 max-w-[520px] ${CAPTION_LINE}`}>
          {stop.rule_text}
        </p>
      )}
      <p className="mx-auto mt-4 max-w-[520px] text-sm text-muted-foreground">
        {`Generate again writes "${section}" afresh from your inputs. If it stops again, look at what "${section}" is written from in ${stageName}.`}
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Button onClick={retry.onRetry} disabled={retry.pending}>
          {retry.pending ? "Starting again…" : "Generate again"}
        </Button>
        <MaybeLink
          to={stagePath(taskId, stage)}
          className={`inline-flex ${buttonChrome({ variant: "secondary" })}`}
        >
          {`Go to ${stageName}`}
        </MaybeLink>
      </div>
      <Refusal retry={retry} />
    </Ended>
  );
}

function Failed({ retry }: { retry: RetryControl }) {
  return (
    <Ended
      title="The draft could not be generated"
      line="Something went wrong on our side. Your inputs are kept. Try again."
      icon={
        <IconTile tone="destructive" shape="round" size="md">
          <CircleX />
        </IconTile>
      }
    >
      <Button className="mt-6" onClick={retry.onRetry} disabled={retry.pending}>
        {retry.pending ? "Starting again…" : "Try again"}
      </Button>
      <Refusal retry={retry} />
    </Ended>
  );
}

/**
 * A run the draft store refused. No input of the attorney's causes it, and
 * every new run fails the same way until the store is set up, so there is no
 * Try again: each try would spend model calls before it fails.
 */
function NotSaved() {
  return (
    <Ended
      title="The draft could not be saved"
      line="This is not caused by your inputs. Generating the draft again fails the same way until our team fixes it. Contact support. Your inputs are kept."
      icon={
        <IconTile tone="destructive" shape="round" size="md">
          <CircleX />
        </IconTile>
      }
    />
  );
}

/**
 * One draft run as the attorney reads it: being created, ready to download,
 * stopped by a check, or failed. The failure's own words are the operator's
 * and never reach the screen.
 */
export function RunView({
  run,
  taskId,
  download,
  retry,
  unreachable = false,
}: {
  run: GenerateRunResponse;
  taskId: string;
  download: DownloadControl;
  retry: RetryControl;
  /** Polls have failed in a row while the run is in flight. */
  unreachable?: boolean;
}) {
  switch (run.state) {
    case "queued":
    case "running":
      return <Processing run={run} unreachable={unreachable} />;
    case "ready":
      return (
        <Ready
          taskId={taskId}
          bundled={run.artifacts.some((file) => file.kind === "bundle")}
          drawings={run.artifacts.some((file) => file.kind === "drawings")}
          download={download}
        />
      );
    case "stopped":
      return run.stop === null ? (
        <Failed retry={retry} />
      ) : (
        <Stopped stop={run.stop} taskId={taskId} retry={retry} />
      );
    case "failed":
      return run.failure === "store_unavailable" ? (
        <NotSaved />
      ) : (
        <Failed retry={retry} />
      );
  }
}
